import type React from "react"
import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Snackbar,
  IconButton,
  Tooltip,
} from "@mui/material"
import {
  Description as DocumentIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Phone as PhoneIcon,
  Person as PersonIcon,
  Lightbulb as LightbulbIcon,
  Image as ImageIcon,
  Warning as WarningIcon,
  Upload,
  Delete,
} from "@mui/icons-material"
import {
  getContactByPhone,
  createPropertyOwnerDocument2,
  getDocumentsByPropertyId,
  updateContact,
  type CreatePropertyOwnerDocumentData,
} from "../../../services/documentsService"
import CreateContactModal from "../option/CreateContactModal"
import { useAuth } from "../../../context/AuthContext"
import { savePhotosToIndexedDB, getPhotosFromIndexedDB, deleteAllPhotosFromIndexedDB } from "../../../utils/indexDB"

interface DocumentsScreenProps {
  propertyId?: number
  onDataChange?: (data: any) => void
  onSave?: () => Promise<void>
  validationErrors?: ValidationErrors
  showValidation?: boolean
}

interface ContactData {
  id: number
  fullName: string
  email: string
  phone: string
  address: string
}

interface DocumentData {
  id: number
  propertyId: number
  houseAddress: string
  cadastralReference: string
  hasDebts: boolean
  municipalTaxesStatus: string
  preferredSaleMethod: string
  bankName: string
  commissionPercentage: number
  contact?: ContactData
}

interface ValidationErrors {
  houseAddress?: string
  municipalTaxesStatus?: string
  preferredSaleMethod?: string
  bankName?: string
  commissionPercentage?: string
  cadastralDocument?: string
}

const isImageUrl = (url: string): boolean => {
  if (!url) return false

  try {
    new URL(url)
  } catch {
    return false
  }

  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i
  const imageServices = /(amazonaws\.com|cloudinary\.com|imgur\.com|blob\.v0\.dev)/i

  return imageExtensions.test(url) || imageServices.test(url)
}

const DocumentImagePreview: React.FC<{
  imageUrl: string
  onDelete?: () => void
}> = ({ imageUrl, onDelete }) => {
  const [imageError, setImageError] = useState(false)
  const [imageLoading, setImageLoading] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  const handleImageLoad = () => {
    setImageLoading(false)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageLoading(false)
    setImageError(true)
  }

  const handleImageLoadStart = () => {
    setImageLoading(true)
    setImageError(false)
  }

  if (!isImageUrl(imageUrl)) return null

  return (
    <>
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              color: "primary.main",
              fontWeight: "medium",
            }}
          >
            <ImageIcon sx={{ mr: 1 }} />
            Documento Catastral
          </Typography>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 300,
              width: "100%",
              bgcolor: "grey.50",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "grey.200",
              overflow: "hidden",
              cursor: imageError ? "default" : "pointer",
              transition: "transform 0.2s ease",
              "&:hover": {
                transform: imageError ? "none" : "scale(1.02)",
              },
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {imageLoading && (
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1,
                  zIndex: 2,
                }}
              >
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Cargando documento...
                </Typography>
              </Box>
            )}

            {imageError ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                  color: "error.main",
                }}
              >
                <ImageIcon sx={{ fontSize: 64, opacity: 0.5 }} />
                <Typography variant="h6" color="error.main" textAlign="center">
                  Error al cargar el documento
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No se pudo cargar la imagen del documento
                </Typography>
              </Box>
            ) : (
              <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                <img
                  src={imageUrl || "/placeholder.svg"}
                  alt="Documento catastral"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  onLoadStart={handleImageLoadStart}
                  style={{
                    width: "100%",
                    maxHeight: "600px",
                    objectFit: "contain",
                    borderRadius: "8px",
                    display: imageLoading ? "none" : "block",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                />

                {/* Overlay con botones que aparece al hacer hover */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: isHovered ? "rgba(0, 0, 0, 0.3)" : "transparent",
                    transition: "background 0.3s ease",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "flex-end",
                    p: 2,
                    gap: 1,
                  }}
                >
                  {/* Botón de eliminar */}
                  <Tooltip title="Eliminar documento">
                    <IconButton
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        onDelete?.()
                      }}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                        color: "error.main",
                        width: 40,
                        height: 40,
                        opacity: isHovered ? 1 : 0.7,
                        transform: isHovered ? "scale(1.1)" : "scale(1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          bgcolor: "error.main",
                          color: "white",
                          transform: "scale(1.2)",
                        },
                        boxShadow: 3,
                        zIndex: 3,
                      }}
                    >
                      <Delete sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              Usa los botones en la esquina superior derecha para eliminar el documento.
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    </>
  )
}

const ImageUploadSection: React.FC<{
  onImageSelect: (event: React.ChangeEvent<HTMLInputElement>) => void
  selectedImage: File | null
  imagePreview: string | null
  imageUploading: boolean
  onRemoveImage: () => void
  hasError?: boolean
}> = ({ onImageSelect, selectedImage, imagePreview, imageUploading, onRemoveImage, hasError }) => {
  if (selectedImage) {
    return (
      <>
        <Box
          sx={{
            position: "relative",
            p: 2,
            bgcolor: "success.50",
            border: "2px solid",
            borderColor: "success.main",
            borderRadius: 2,
            mb: 2,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: "medium", color: "success.dark" }}>
            ✓ Imagen seleccionada - Lista para guardar
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {imagePreview && selectedImage.type.startsWith("image/") ? (
              <Box
                sx={{
                  position: "relative",
                }}
              >
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Preview"
                  sx={{
                    width: 80,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 1,
                    border: "2px solid",
                    borderColor: "success.main",
                  }}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "success.100",
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2rem",
                  border: "2px solid",
                  borderColor: "success.main",
                }}
              >
                📄
              </Box>
            )}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight="medium" color="success.dark">
                {selectedImage.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(selectedImage.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "success.dark" }}>
                La imagen se guardará al crear/actualizar el documento
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Cambiar imagen seleccionada">
            <IconButton
              onClick={onRemoveImage}
              disabled={imageUploading}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "white",
                color: "error.main",
                width: 32,
                height: 32,
                border: "2px solid",
                borderColor: "error.main",
                "&:hover": {
                  bgcolor: "error.main",
                  color: "white",
                  borderColor: "error.dark",
                },
                boxShadow: 2,
              }}
            >
              <Delete sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {imageUploading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Procesando imagen...
              </Typography>
            </Box>
          )}
        </Box>
      </>
    )
  }

  return (
    <Box
      sx={{
        border: "2px dashed",
        borderColor: hasError ? "error.main" : "grey.400",
        borderRadius: 2,
        p: 4,
        textAlign: "center",
        cursor: "pointer",
        transition: "all 0.2s ease",
        backgroundColor: hasError ? "error.50" : "grey.50",
        "&:hover": {
          borderColor: hasError ? "error.dark" : "primary.main",
          bgcolor: hasError ? "error.100" : "action.hover",
          transform: "scale(1.02)",
        },
        mb: 2,
        minHeight: "200px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
      component="label"
    >
      <Upload sx={{ fontSize: 48, color: hasError ? "error.main" : "primary.main", mb: 2 }} />
      <Typography variant="h6" color={hasError ? "error.main" : "primary.main"} sx={{ mb: 1 }}>
        Seleccionar Documento Catastral *
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Haz clic aquí para seleccionar una imagen o documento PDF
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Formatos soportados: JPG, PNG, PDF (máx. 10MB)
      </Typography>
      {hasError && (
        <Typography variant="caption" color="error.main" sx={{ mt: 1, fontWeight: "medium" }}>
          El documento catastral es requerido
        </Typography>
      )}
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={onImageSelect}
        style={{ display: "none" }}
        disabled={imageUploading}
      />
    </Box>
  )
}

const DocumentsScreen: React.FC<DocumentsScreenProps> = ({
  propertyId,
  onDataChange,
  validationErrors: externalValidationErrors,
  showValidation: externalShowValidation,
}) => {
  // Estados principales
  const [documentData, setDocumentData] = useState<DocumentData | null>(null)
  const [contactData, setContactData] = useState<ContactData | null>(null)
  const [loading, setLoading] = useState(false)
  const [documentLoadError, setDocumentLoadError] = useState<string | null>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState("")
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning">("success")
  const { userToken } = useAuth()

  // Estados para formularios
  const [phoneNumber, setPhoneNumber] = useState("")
  const [editableData, setEditableData] = useState<Partial<DocumentData>>({})
  const [phoneNotFound, setPhoneNotFound] = useState(false)

  const [documentChanges, setDocumentChanges] = useState<any>({})

  // Estados para modales
  const [createContactModalOpen, setCreateContactModalOpen] = useState(false)
  const [showCreateDocumentForm, setShowCreateDocumentForm] = useState(false)

  // Estados para manejo de imágenes
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [imageUploading, setImageUploading] = useState(false)
  const [showExistingImage, setShowExistingImage] = useState(true)

  // Estados para validación
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  useEffect(() => {
    loadExistingImage()
  }, [])

  const loadExistingImage = async () => {
    try {
      const photos = await getPhotosFromIndexedDB()
      if (photos.length > 0) {
        const photo = photos[0]
        setSelectedImage(photo)
        setImagePreview(URL.createObjectURL(photo))
      }
    } catch (error) {
      console.error("Error loading existing image:", error)
    }
  }

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setImageUploading(true)
      try {
        await deleteAllPhotosFromIndexedDB()

        const documentId = documentData?.id || propertyId || Date.now()
        const timestamp = Date.now()
        const fileExtension = file.name.split(".").pop() || "jpg"
        const autoGeneratedName = `documento_catastral_${documentId}_${timestamp}.${fileExtension}`

        const renamedFile = new File([file], autoGeneratedName, { type: file.type })

        await savePhotosToIndexedDB([renamedFile])

        setSelectedImage(renamedFile)
        setImagePreview(URL.createObjectURL(renamedFile))

        // Limpiar error de validación del documento
        setValidationErrors((prev) => ({ ...prev, cadastralDocument: undefined }))

        if (documentData?.id) {
          const updatedChanges = {
            ...documentChanges,
            id: documentData.id,
            hasImagePending: true,
          }
          setDocumentChanges(updatedChanges)
          onDataChange?.(updatedChanges)
        }

        showSnackbar(`Imagen cargada como: ${autoGeneratedName}`, "success")
      } catch (error) {
        console.error("Error al guardar imagen:", error)
        showSnackbar("Error al cargar la imagen", "error")
      } finally {
        setImageUploading(false)
      }
    }
  }

  const handleRemoveImage = async () => {
    try {
      await deleteAllPhotosFromIndexedDB()
      setSelectedImage(null)
      setImagePreview(null)
      setShowExistingImage(false)

      if (documentData?.id) {
        const updatedChanges = {
          ...documentChanges,
          id: documentData.id,
          hasImagePending: false,
        }
        setDocumentChanges(updatedChanges)
        onDataChange?.(updatedChanges)
      }

      if (documentData?.cadastralReference) {
        setEditableData((prev) => ({
          ...prev,
          cadastralReference: "",
        }))
      }

      showSnackbar("Imagen eliminada", "success")
    } catch (error) {
      console.error("Error al eliminar imagen:", error)
      showSnackbar("Error al eliminar la imagen", "error")
    }
  }

  const loadDocuments = useCallback(async () => {
    if (!propertyId) return

    setLoading(true)
    setDocumentLoadError(null)

    try {
      const data = await getDocumentsByPropertyId(propertyId.toString())

      if (data) {
        setDocumentData(data)
        setEditableData(data)
        setShowExistingImage(true)
        setDocumentChanges({ id: data.id })

        if (data.contact) {
          setContactData(data.contact)
        }
      } else {
        // API devolvió null sin error
        setDocumentData(null)
        setDocumentLoadError(
          "No se encontró ningún documento. Por favor, busca o crea un contacto del dueño para crear el documento.",
        )
        showSnackbar("No se encontró documento. Debes buscar o crear un contacto del dueño para continuar.", "warning")
      }
    } catch (err: any) {
      console.error("Error al cargar documentos:", err)

      if (err.response && err.response.status === 404) {
        // Documento no encontrado
        setDocumentData(null)
        setDocumentLoadError(
          "No se encontró ningún documento. Por favor, busca o crea un contacto del dueño para crear el documento.",
        )
        showSnackbar("No se encontró documento. Debes buscar o crear un contacto del dueño para continuar.", "warning")
      } else if (err.response && err.response.status >= 500) {
        // Error del servidor
        setDocumentData(null)
        setDocumentLoadError(
          "Error temporal del servidor. Puedes continuar trabajando con contactos mientras se soluciona.",
        )
        showSnackbar("Error del servidor al cargar documentos. Puedes continuar trabajando con contactos.", "warning")
      } else if (err.code === "NETWORK_ERROR" || !err.response) {
        // Error de red
        setDocumentData(null)
        setDocumentLoadError("Problema de conexión. Puedes trabajar con contactos mientras se restablece la conexión.")
        showSnackbar("Problema de conexión. Puedes trabajar con contactos mientras tanto.", "warning")
      } else {
        // Cualquier otro error
        setDocumentData(null)
        setDocumentLoadError(
          "Ocurrió un error inesperado al cargar documentos. Puedes continuar trabajando con contactos.",
        )
        showSnackbar("Error inesperado al cargar documentos. Puedes continuar trabajando con contactos.", "warning")
      }
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => {
    if (propertyId) {
      loadDocuments()
    }
  }, [propertyId, loadDocuments])

  const searchContact = async () => {
    if (!phoneNumber.trim()) {
      showSnackbar("Ingresa un número de teléfono", "error")
      return
    }

    // Validación del formato del teléfono
    const phoneRegex = /^[0-9]{8,15}$/
    if (!phoneRegex.test(phoneNumber.trim())) {
      showSnackbar("Por favor, digita un número de teléfono válido (8-15 dígitos).", "error")
      // Set phoneNotFound to true even when validation fails, so user can create a contact
      setPhoneNotFound(true)
      return
    }

    setLoading(true)
    setPhoneNotFound(false)
    try {
      const contact = await getContactByPhone(phoneNumber)
      if (contact) {
        setContactData(contact)
        setPhoneNotFound(false)
        showSnackbar(`Contacto encontrado: ${contact.fullName}`, "success")
      } else {
        setContactData(null)
        setPhoneNotFound(true)
      }
    } catch (error) {
      setContactData(null)
      setPhoneNotFound(true)
      showSnackbar("Error al buscar contacto", "error")
    } finally {
      setLoading(false)
    }
  }

  // 🔧 FUNCIÓN MEJORADA: Validación más estricta de campos requeridos
  const validateRequiredFields = (): boolean => {
    const errors: ValidationErrors = {}
    let hasErrors = false

    // Función auxiliar para validar strings
    const isValidString = (value: any): boolean => {
      return value !== null && value !== undefined && typeof value === "string" && value.trim().length > 0
    }

    // Función auxiliar para validar números
    const isValidNumber = (value: any): boolean => {
      return value !== null && value !== undefined && typeof value === "number" && value > 0 && !isNaN(value)
    }

    // Validar dirección - MÁS ESTRICTO
    if (!isValidString(editableData.houseAddress)) {
      errors.houseAddress = "La dirección es requerida y no puede estar vacía"
      hasErrors = true
    }

    // Validar estado de impuestos - MÁS ESTRICTO
    if (!isValidString(editableData.municipalTaxesStatus)) {
      errors.municipalTaxesStatus = "El estado de impuestos es requerido y no puede estar vacío"
      hasErrors = true
    }

    // Validar método de venta - MÁS ESTRICTO
    if (!isValidString(editableData.preferredSaleMethod)) {
      errors.preferredSaleMethod = "El método de venta es requerido y no puede estar vacío"
      hasErrors = true
    }

    // Validar banco - MÁS ESTRICTO
    if (!isValidString(editableData.bankName)) {
      errors.bankName = "El banco es requerido y no puede estar vacío"
      hasErrors = true
    }

    // Validar porcentaje de comisión - MÁS ESTRICTO
    if (!isValidNumber(editableData.commissionPercentage)) {
      errors.commissionPercentage = "El porcentaje de comisión es requerido y debe ser un número mayor a 0"
      hasErrors = true
    }

    // Validar documento catastral - MÁS ESTRICTO
    if (!selectedImage || selectedImage.size === 0) {
      errors.cadastralDocument = "El documento catastral es requerido"
      hasErrors = true
    }

    setValidationErrors(errors)
    setShowValidationErrors(true)

    return !hasErrors
  }

  const createDocument = async () => {
    // Validación básica
    if (!contactData || !propertyId) {
      showSnackbar("Necesitas un contacto y property ID válidos", "error")
      return
    }

    if (!validateRequiredFields()) {
      // Scroll hacia arriba para mostrar los errores
      window.scrollTo({ top: 0, behavior: "smooth" })
      return
    }

    const realtorProfile = userToken?.user.profiles.find((profile) => profile.roleId === 3)
    const idRealtor = realtorProfile?.realtor?.id

    if (!idRealtor) {
      showSnackbar("No se pudo determinar el broker logueado", "error")
      return
    }

    // 🔧 VALIDACIÓN ADICIONAL: Verificar una vez más antes de enviar
    const finalValidation = {
      houseAddress: editableData.houseAddress?.trim(),
      municipalTaxesStatus: editableData.municipalTaxesStatus?.trim(),
      preferredSaleMethod: editableData.preferredSaleMethod?.trim(),
      bankName: editableData.bankName?.trim(),
      commissionPercentage: editableData.commissionPercentage,
      selectedImage: selectedImage,
    }

    // Verificación final estricta
    if (
      !finalValidation.houseAddress ||
      !finalValidation.municipalTaxesStatus ||
      !finalValidation.preferredSaleMethod ||
      !finalValidation.bankName ||
      !finalValidation.commissionPercentage ||
      finalValidation.commissionPercentage <= 0 ||
      !finalValidation.selectedImage
    ) {
      showSnackbar("Error: Algunos campos requeridos están vacíos. Por favor, revisa el formulario.", "error")
      validateRequiredFields() // Mostrar errores específicos
      return
    }

    setLoading(true)
    try {
      const finalCadastralReference = editableData.cadastralReference

      if (selectedImage) {
        const timestamp = Date.now()
        const fileExtension = selectedImage.name.split(".").pop() || "jpg"
        const autoGeneratedName = `documento_catastral_${propertyId}_${timestamp}.${fileExtension}`

        const renamedFile = new File([selectedImage], autoGeneratedName, { type: selectedImage.type })

        await deleteAllPhotosFromIndexedDB()
        await savePhotosToIndexedDB([renamedFile])

        setSelectedImage(renamedFile)
      }

      const documentDataToSend: CreatePropertyOwnerDocumentData = {
        propertyId: propertyId,
        contactId: contactData.id,
        createdByBrokerId: idRealtor,
        hasDebts: editableData.hasDebts || false,
        commissionPercentage: finalValidation.commissionPercentage!,
        houseAddress: finalValidation.houseAddress!,
        cadastralReference: finalCadastralReference,
        municipalTaxesStatus: finalValidation.municipalTaxesStatus!,
        preferredSaleMethod: finalValidation.preferredSaleMethod!,
        bankName: finalValidation.bankName!,
      }
      const response = await createPropertyOwnerDocument2(documentDataToSend)
      if (response) {
        showSnackbar("Documento creado exitosamente", "success")
        setShowCreateDocumentForm(false)
        setDocumentLoadError(null)

        // Limpiar validaciones
        setValidationErrors({})
        setShowValidationErrors(false)

        await deleteAllPhotosFromIndexedDB()
        setSelectedImage(null)
        setImagePreview(null)

        loadDocuments()
      } else {
        showSnackbar("Error al crear documento", "error")
      }
    } catch (error: any) {
      console.error("❌ Error al crear documento:", error)
      if (error.response) {
        console.error("📋 Respuesta de error del servidor:", error.response.data)
        showSnackbar(`Error al crear documento: ${error.response.data || error.message}`, "error")
      } else {
        showSnackbar("Error al crear documento", "error")
      }
    } finally {
      setLoading(false)
    }
  }

  const updateContactAssociation = async () => {
    if (!documentData || !contactData) return

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append("DocumentId", documentData.id.toString())
      formData.append("ContactId", contactData.id.toString())

      const response = await updateContact(formData)
      if (response && response.data) {
        setDocumentData(response.data)
        setEditableData(response.data)
        showSnackbar("Contacto del dueño asociado exitosamente", "success")
        setDocumentLoadError(null)
        loadDocuments()
      } else {
        showSnackbar("Error al asociar contacto", "error")
      }
    } catch (error) {
      console.error("Error al asociar contacto:", error)
      showSnackbar("Error al asociar contacto", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleContactCreated = async (newContact: ContactData) => {
    if (newContact.id > 1000000000000) {
      try {
        setLoading(true)
        const realContact = await getContactByPhone(newContact.phone)
        if (realContact) {
          setContactData(realContact)
          setPhoneNumber(realContact.phone)
          setPhoneNotFound(false)
          showSnackbar(`Contacto creado: ${realContact.fullName}`, "success")
        } else {
          setContactData(newContact)
          setPhoneNumber(newContact.phone)
          setPhoneNotFound(false)
          showSnackbar(`Contacto creado: ${newContact.fullName} (ID temporal)`, "warning")
        }
      } catch (error) {
        console.error("Error al buscar contacto recién creado:", error)
        setContactData(newContact)
        setPhoneNumber(newContact.phone)
        setPhoneNotFound(false)
        showSnackbar(`Contacto creado: ${newContact.fullName}`, "success")
      } finally {
        setLoading(false)
      }
    } else {
      setContactData(newContact)
      setPhoneNumber(newContact.phone)
      setPhoneNotFound(false)
      showSnackbar(`Contacto creado: ${newContact.fullName}`, "success")
    }
  }

  const showSnackbar = (message: string, severity: "success" | "error" | "warning") => {
    setSnackbarMessage(message)
    setSnackbarSeverity(severity)
    setSnackbarOpen(true)
  }

  const handleEditableChange = (field: keyof DocumentData, value: any) => {
    // Prevenir valores negativos en el porcentaje de comisión
    if (field === "commissionPercentage" && typeof value === "number" && value < 0) {
      return // No actualizar si el valor es negativo
    }

    setEditableData((prev) => {
      const newData = {
        ...prev,
        [field]: value,
      }

      onDataChange?.(newData)

      return newData
    })

    // Limpiar errores de validación cuando el usuario empiece a escribir
    if (showValidationErrors && validationErrors[field as keyof ValidationErrors]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }))
    }
  }

  const handlePhoneNumberChange = (value: string) => {
    setPhoneNumber(value)
    if (phoneNotFound) {
      setPhoneNotFound(false)
    }
    if (contactData) {
      setContactData(null)
    }
  }

  const retryLoadDocuments = () => {
    setDocumentLoadError(null)
    loadDocuments()
  }

  const noPropertySelected = !propertyId

  if (loading && !documentData && !documentLoadError && propertyId) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px", p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Cargando documentos...</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "900px",
        p: 3,
      }}
    >
      <Typography variant="h5" sx={{ mb: 3, textAlign: "center" }}>
        Documentos y Contactos {propertyId ? `- Propiedad #${propertyId}` : "- Sin Propiedad Seleccionada"}
      </Typography>

      {noPropertySelected && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            No hay una propiedad seleccionada. Puedes buscar contactos, pero necesitarás seleccionar una propiedad para
            crear documentos.
          </Typography>
        </Alert>
      )}

      {documentLoadError && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={retryLoadDocuments}>
              Reintentar
            </Button>
          }
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <WarningIcon sx={{ mr: 1 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                {documentLoadError.includes("No se encontró")
                  ? "Documento no encontrado"
                  : "Problema al cargar documentos"}
              </Typography>
              <Typography variant="body2">{documentLoadError}</Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {/* Sección de búsqueda de contacto */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
            <SearchIcon sx={{ mr: 1 }} />
            Buscar Contacto Del Dueño
          </Typography>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <TextField
              label="Número de teléfono del dueño"
              value={phoneNumber}
              onChange={(e) => handlePhoneNumberChange(e.target.value)}
              placeholder="Busca el número de teléfono del dueño"
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              onClick={searchContact}
              disabled={loading || !phoneNumber.trim()}
              startIcon={<SearchIcon />}
            >
              Buscar
            </Button>
            <Tooltip
              title={
                phoneNotFound && phoneNumber.trim() ? "Crear nuevo contacto con este número" : "Agregar nuevo contacto"
              }
            >
              <IconButton
                color="primary"
                onClick={() => setCreateContactModalOpen(true)}
                sx={{
                  bgcolor: phoneNotFound && phoneNumber.trim() ? "success.main" : "primary.main",
                  color: "white",
                  "&:hover": { bgcolor: phoneNotFound && phoneNumber.trim() ? "success.dark" : "primary.dark" },
                  animation: phoneNotFound && phoneNumber.trim() ? "heartbeat 1.5s ease-in-out infinite" : "none",
                  "@keyframes heartbeat": {
                    "0%": {
                      transform: "scale(1)",
                      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0.7)",
                    },
                    "70%": {
                      transform: "scale(1.1)",
                      boxShadow: "0 0 0 10px rgba(76, 175, 80, 0)",
                    },
                    "100%": {
                      transform: "scale(1)",
                      boxShadow: "0 0 0 0 rgba(76, 175, 80, 0)",
                    },
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {phoneNotFound && phoneNumber.trim() && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Dueños con este número de teléfono no existe, por favor crea uno nuevo
              </Typography>
            </Alert>
          )}

          {contactData && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#e8f5e8", borderRadius: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", display: "flex", alignItems: "center" }}>
                  <PhoneIcon sx={{ mr: 1, fontSize: 18 }} />
                  Dueño encontrado:
                </Typography>
                {documentData && !noPropertySelected && !documentLoadError && (
                  <Button size="small" variant="outlined" onClick={updateContactAssociation} disabled={loading}>
                    Asociar al Documento
                  </Button>
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Nombre:</strong> {contactData.fullName}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {contactData.email}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Teléfono:</strong> {contactData.phone}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Dirección:</strong> {contactData.address}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Mostrar documento existente, formulario de creación, o mensaje según el caso */}
      {!noPropertySelected && documentData && !documentLoadError ? (
        // Caso 1: Hay propiedad Y hay documento Y no hay error - Mostrar documento existente
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center" }}>
                <DocumentIcon sx={{ mr: 1 }} />
                Documento ID: {documentData.id}
              </Typography>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                  Información de la Propiedad
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Dirección"
                    value={editableData.houseAddress || ""}
                    onChange={(e) => handleEditableChange("houseAddress", e.target.value)}
                    fullWidth
                    margin="dense"
                    multiline
                    rows={2}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Estado de Impuestos"
                    value={editableData.municipalTaxesStatus || ""}
                    onChange={(e) => handleEditableChange("municipalTaxesStatus", e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 2 }}>
                  Información Financiera
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Banco"
                    value={editableData.bankName || ""}
                    onChange={(e) => handleEditableChange("bankName", e.target.value)}
                    fullWidth
                    margin="dense"
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <TextField
                    label="Porcentaje de Comisión"
                    type="number"
                    value={editableData.commissionPercentage || ""}
                    onChange={(e) => {
                      const value = Number(e.target.value)
                      if (value >= 0) {
                        // Solo permitir valores positivos
                        handleEditableChange("commissionPercentage", value)
                      }
                    }}
                    fullWidth
                    margin="dense"
                    InputProps={{
                      endAdornment: "%",
                      inputProps: { min: 0, step: 0.01 }, // Establecer mínimo en 0
                    }}
                  />
                </Box>

                {documentData.contact && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1, display: "flex", alignItems: "center" }}
                    >
                      <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                      Dueño Asociado:
                    </Typography>
                    <Typography variant="body2">{documentData.contact.fullName}</Typography>
                    <Typography variant="body2">{documentData.contact.phone}</Typography>
                    <Typography variant="body2">{documentData.contact.email}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {/* Gestión de imagen catastral mejorada */}
            <Box sx={{ mt: 3, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                <ImageIcon sx={{ mr: 1 }} />
                Gestión de Documento Catastral
              </Typography>

              {/* Si hay imagen existente en el documento, mostrarla */}
              {documentData?.cadastralReference &&
              isImageUrl(documentData.cadastralReference) &&
              !selectedImage &&
              showExistingImage ? (
                <DocumentImagePreview imageUrl={documentData.cadastralReference} onDelete={handleRemoveImage} />
              ) : (
                /* Si no hay imagen existente o se eliminó, mostrar sección de subida */
                <ImageUploadSection
                  onImageSelect={handleImageSelect}
                  selectedImage={selectedImage}
                  imagePreview={imagePreview}
                  imageUploading={imageUploading}
                  onRemoveImage={handleRemoveImage}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      ) : !noPropertySelected && (!documentData || documentLoadError) ? (
        // Caso 2: Hay propiedad pero NO hay documento O hay error - Mostrar opción de crear
        <Card>
          <CardContent>
            {!showCreateDocumentForm ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "300px",
                  textAlign: "center",
                }}
              >
                <DocumentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: "text.secondary" }}>
                  {documentLoadError ? "Problema al cargar documentos" : "Propiedad sin documentos"}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                  {documentLoadError
                    ? "Hay un problema temporal, pero puedes crear un nuevo documento."
                    : "Esta propiedad no tiene documentos asociados."}
                </Typography>

                <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                  <Tooltip
                    title={
                      !contactData ? "Primero debes buscar o crear un contacto" : "Crear documento para esta propiedad"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowCreateDocumentForm(true)}
                        disabled={!contactData}
                        sx={{
                          bgcolor: contactData ? "primary.main" : "grey.300",
                          "&:hover": {
                            bgcolor: contactData ? "primary.dark" : "grey.300",
                          },
                        }}
                      >
                        Crear Documento
                      </Button>
                    </span>
                  </Tooltip>

                  {documentLoadError && (
                    <Button variant="outlined" onClick={retryLoadDocuments} disabled={loading}>
                      Reintentar Carga
                    </Button>
                  )}
                </Box>

                {!contactData && (
                  <Box sx={{ mt: 2, display: "flex", alignItems: "center", color: "text.secondary" }}>
                    <LightbulbIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="caption">
                      Busca o crea un contacto primero para poder crear documentos
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              // Formulario de creación de documento
              <Box>
                <Typography variant="h6" sx={{ mb: 2, display: "flex", alignItems: "center" }}>
                  <AddIcon sx={{ mr: 1 }} />
                  Crear Nuevo Documento
                </Typography>

                {/* Alerta de campos requeridos */}
                {showValidationErrors && Object.keys(validationErrors).length > 0 && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                      ⚠️ Por favor, completa los siguientes campos requeridos:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                      {validationErrors.houseAddress && <li>Dirección</li>}
                      {validationErrors.municipalTaxesStatus && <li>Estado de Impuestos</li>}
                      {validationErrors.preferredSaleMethod && <li>Método de Venta</li>}
                      {validationErrors.bankName && <li>Banco</li>}
                      {validationErrors.commissionPercentage && <li>Porcentaje de Comisión</li>}
                      {validationErrors.cadastralDocument && <li>Documento Catastral</li>}
                    </ul>
                  </Alert>
                )}

                {contactData && (
                  <Box sx={{ mb: 3, p: 2, bgcolor: "#e8f5e8", borderRadius: 1 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1, display: "flex", alignItems: "center" }}
                    >
                      <PersonIcon sx={{ mr: 1, fontSize: 18 }} />
                      Contacto Asociado:
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Nombre:</strong> {contactData.fullName}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong> {contactData.email}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          <strong>Teléfono:</strong> {contactData.phone}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Dirección:</strong> {contactData.address}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Dirección *"
                      value={editableData.houseAddress || ""}
                      onChange={(e) => handleEditableChange("houseAddress", e.target.value)}
                      fullWidth
                      margin="dense"
                      multiline
                      rows={2}
                      required
                      error={showValidationErrors && !!validationErrors.houseAddress}
                      helperText={showValidationErrors && validationErrors.houseAddress}
                      inputProps={{ minLength: 1 }}
                    />

                    <TextField
                      label="Estado de Impuestos *"
                      value={editableData.municipalTaxesStatus || ""}
                      onChange={(e) => handleEditableChange("municipalTaxesStatus", e.target.value)}
                      fullWidth
                      margin="dense"
                      required
                      error={showValidationErrors && !!validationErrors.municipalTaxesStatus}
                      helperText={showValidationErrors && validationErrors.municipalTaxesStatus}
                      inputProps={{ minLength: 1 }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Método de Venta *"
                      value={editableData.preferredSaleMethod || ""}
                      onChange={(e) => handleEditableChange("preferredSaleMethod", e.target.value)}
                      fullWidth
                      margin="dense"
                      required
                      error={showValidationErrors && !!validationErrors.preferredSaleMethod}
                      helperText={showValidationErrors && validationErrors.preferredSaleMethod}
                      inputProps={{ minLength: 1 }}
                    />

                    <TextField
                      label="Banco *"
                      value={editableData.bankName || ""}
                      onChange={(e) => handleEditableChange("bankName", e.target.value)}
                      fullWidth
                      margin="dense"
                      required
                      error={showValidationErrors && !!validationErrors.bankName}
                      helperText={showValidationErrors && validationErrors.bankName}
                      inputProps={{ minLength: 1 }}
                    />

                    <TextField
                      label="Porcentaje de Comisión *"
                      type="number"
                      value={editableData.commissionPercentage || ""}
                      onChange={(e) => {
                        const value = Number(e.target.value)
                        if (value >= 0) {
                          // Solo permitir valores positivos
                          handleEditableChange("commissionPercentage", value)
                        }
                      }}
                      fullWidth
                      margin="dense"
                      required
                      error={showValidationErrors && !!validationErrors.commissionPercentage}
                      helperText={showValidationErrors && validationErrors.commissionPercentage}
                      InputProps={{
                        endAdornment: "%",
                        inputProps: { min: 0, step: 0.01 }, // Establecer mínimo en 0
                      }}
                    />

                    <FormControlLabel
                      control={
                        <Switch
                          checked={editableData.hasDebts || false}
                          onChange={(e) => handleEditableChange("hasDebts", e.target.checked)}
                        />
                      }
                      label="Tiene deudas"
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>

                {/* Sección de subida de imagen para nuevo documento */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: "medium" }}>
                    Documento Catastral *
                  </Typography>

                  <ImageUploadSection
                    onImageSelect={handleImageSelect}
                    selectedImage={selectedImage}
                    imagePreview={imagePreview}
                    imageUploading={imageUploading}
                    onRemoveImage={handleRemoveImage}
                    hasError={showValidationErrors && !!validationErrors.cadastralDocument}
                  />

                  {showValidationErrors && validationErrors.cadastralDocument && (
                    <Typography variant="caption" color="error.main" sx={{ mt: 1, display: "block" }}>
                      {validationErrors.cadastralDocument}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    onClick={createDocument}
                    disabled={loading}
                    startIcon={<AddIcon />}
                    sx={{
                      minWidth: 150,
                      fontWeight: "bold",
                    }}
                  >
                    {loading ? "Creando..." : "Crear Documento"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setShowCreateDocumentForm(false)
                      setValidationErrors({})
                      setShowValidationErrors(false)
                    }}
                  >
                    Cancelar
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        // Caso 3: NO hay propiedad seleccionada
        <Card>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
                textAlign: "center",
              }}
            >
              <DocumentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1, color: "text.secondary" }}>
                Sin Propiedad Seleccionada
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
                Selecciona una propiedad para ver o crear documentos.
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Mientras tanto, puedes buscar y crear el contacto para el dueño usando la sección de arriba.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modal para crear contacto */}
      <CreateContactModal
        open={createContactModalOpen}
        onClose={() => setCreateContactModalOpen(false)}
        onContactCreated={handleContactCreated}
        initialPhone={phoneNumber}
      />

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default DocumentsScreen
